/* eslint-disable no-use-before-define */
import {
  routeTo,
  GetAssignedBucket
} from '@carvana/purchase-helpers';
import { push } from 'connected-react-router';
import store from '@carvana/purchase-store';
import { wait, configureExperimentsFullstory } from '@carvana/purchase-utils';
import { UpdateStockNumberAndOrVehicleId } from '../../vehicle/ducks/actions';
import * as services from '../services';
import * as types from './action-types';
import UnderwritingActions from './order-state-actions/underwriting-actions';
import OrderPlacedActions from './order-state-actions/order-placed-actions';
import InProgressActions from './order-state-actions/in-progress-actions';

export const GetAssignedExperimentBucket = experimentName => {
  const { userId } = store.getState().core;

  return GetAssignedBucket({
    environment: process.env.NODE_ENV,
    experimentName,
    identifier: userId
  })
    .then(({ assignedBucket }) => assignedBucket)
    .catch(() => 'control');
};

export const DetermineNextStep = steps => {
  let nextStep = steps?.find(x => x.inProgress && x.navigation);

  if (!nextStep) {
    nextStep = steps?.find(x => x.fallbackRoute);
  }

  if (!nextStep) {
    nextStep = steps?.find(x => x.isEnabled && x.navigation);
  }

  return nextStep;
};

const trackConversion = () => dispatch => {
  return dispatch({
    type: '@@analytics/ANALYTICS_TRACK_CONVERSION',
    meta: {
      analytics: {
        type: 'trackConversion',
        name: 'Started Purchase'
      }
    }
  });
};

export const StartPurchaseWithVehicleId = payload => dispatch =>
  dispatch({
    type: types.START_PURCHASE_WITH_VEHICLE_ID,
    async payload() {
      return services.postStartPurchaseWithVehicleId(payload);
    }
  }).then(() => {
    return dispatch(trackConversion());
  });

export const SwitchVehicle = (vehicleId, reason) => (dispatch, getState) => {
  const { dashboard: { isCancelInProcess } = {} } = getState();

  return dispatch({
    type: types.SWITCH_VEHICLE,
    async payload() {
      return isCancelInProcess
        ? Promise.resolve()
        : services.switchVehicle(vehicleId, reason);
    }
  });
};

export const CancelCurrentPurchase = payload => (dispatch, getState) => {
  const { dashboard: { isCancelInProcess } = {} } = getState();

  return dispatch({
    type: types.CANCEL_CURRENT_PURCHASE,
    async payload() {
      return isCancelInProcess
        ? Promise.resolve()
        : services.deleteCancelCurrentPurchase(payload);
    }
  });
};

export const CancelCurrentPurchaseThenNewStartPurchaseWithVehicleId = vehicleId => dispatch =>
  dispatch(SwitchVehicle(vehicleId, 'VehicleLockedByOtherCustomer'))
    .then(() => dispatch(UpdateStockNumberAndOrVehicleId()))
    .then(() => wait(1000));

const loadDashboard = async () => {
  let res = null;
  let availableOptions = {};
  res = await services.getDashboard();
  let orderState = res?.status?.orderState;

  /* if (!Object.keys(res).length) {
    return Promise.resolve(res);
  } */

  if (
    (orderState && orderState === 'Underwriting') ||
    orderState === 'OrderPlaced'
  ) {
    availableOptions = await store
      .dispatch(GetAvailableOptionsForOrder())
      .then(({ value }) => value)
      .catch(() => {});
  }

  if (orderState === 'InProgress') {
    res = await InProgressActions(res, availableOptions);
    orderState = res?.status?.orderState;
  }

  if (orderState === 'Underwriting') {
    res = await UnderwritingActions(res, availableOptions);
    orderState = res?.status?.orderState;
  }

  if (orderState === 'OrderPlaced') {
    res = await OrderPlacedActions(res, availableOptions);
    orderState = res?.status?.orderState;
  }

  await store.dispatch(GetDashboardSummary());

  return Promise.resolve(res);
};

export const GetDashboard = () => dispatch =>
  dispatch({
    type: types.GET_DASHBOARD,
    payload: loadDashboard
  }).catch(err => {
    const isFourOhOneError = err?.messages?.includes('401');
    if (!isFourOhOneError) return dispatch(routeTo('/error'));

    return null;
  });

export const GetDashboardBackgroundLoading = () => dispatch =>
  dispatch({
    type: types.GET_DASHBOARD_NO_LOADING_INDICATOR,
    payload: loadDashboard
  });

export const GetAvailableOptionsForOrder = () => dispatch =>
  dispatch({
    type: types.GET_AVAILABLE_OPTIONS_FOR_ORDER,
    async payload() {
      return services.getAvailableOptionsForOrder();
    }
  });

export const GetDashboardAndRouteToNextStep = ({
  isBackgroundLoading = true
} = {}) => (dispatch, getState) => {
  const isCarvanaMobileAppUserAgent = () =>
    /CarvanaNativeWebView/gi.test(navigator.userAgent);

  if (isCarvanaMobileAppUserAgent()) {
    dispatch(() => pushFinishedQueryParamToHistory(dispatch, getState));
  }

  dispatch(trackUser());

  const dashboardAction = isBackgroundLoading
    ? GetDashboardBackgroundLoading
    : GetDashboard;
  return dispatch(dashboardAction())
    .then(() => {
      return dispatch(routeTo('/dashboard'));
    })
    .catch(error => {
      const isFourOhOneError = error?.message?.includes('401');
      if (!isFourOhOneError) return dispatch(routeTo('/error'));

      return null;
    });
};

const pushFinishedQueryParamToHistory = (dispatch, getState) => {
  const { router: { location } = {} } = getState();
  const searchParams = new URLSearchParams();
  searchParams.set('status', 'finished');
  dispatch(push(`${location?.pathname}?${searchParams.toString()}`));
  return dispatch(push(`${location?.pathname}`));
};

export const GetDashboardSummary = () => dispatch =>
  dispatch({
    type: types.ORDER_SUMMARY_FETCH,
    async payload() {
      return services.getDashboardSummary();
    }
  }).then(({ value }) => value);

export const IsFacebook = () => dispatch =>
  dispatch({
    type: types.GET_IS_FACEBOOK,
    async payload() {
      return services.isFacebook();
    }
  }).then(({ value }) => value);

export const RouteToAppStore = isiOS => {
  const iosAppStoreUrl =
    'https://itunes.apple.com/us/app/carvana-buy-used-cars-online/id1273426583?mt=8';
  const androidAppStoreUrl =
    'https://play.google.com/store/apps/details?id=com.carvana.carvana&hl=en_US';
  if (isiOS) {
    window.location.href = iosAppStoreUrl;
  } else {
    window.location.href = androidAppStoreUrl;
  }
};

const trackUser = () => (dispatch, getState) => {
  const {
    vehicle: { stockNumber } = {},
    core: { userId, email, name, experiments } = {},
    dashboard: { status: { purchaseId } = {} } = {}
  } = getState();

  const fullstoryExperiments = configureExperimentsFullstory(experiments);

  return dispatch({
    type: '@@analytics/ANALYTICS_TRACK_USER',
    payload: Promise.resolve(),
    meta: {
      analytics: {
        type: 'trackUser',
        name: 'Get Dashboard Route Next Step',
        fullstory: {
          userId,
          email,
          name,
          stockNumber,
          purchaseId,
          experiments: fullstoryExperiments
        },
        clients: ['fullstory']
      }
    }
  });
};

export const GetPaymentSummary = () => dispatch =>
  dispatch({
    type: types.GET_PAYMENT_SUMMARY,
    async payload() {
      return services.getPaymentSummary();
    }
  });
